// src/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkUserProfile = async () => {
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, role, status')
          .eq('user_id', user.id)
          .single();

        // Check if user is banned
        if (profile && profile.status === 'banned') {
          try {
            // Check if ban is still active
            const { data: banRecord, error: banError } = await supabase
              .from('user_bans')
              .select('*')
              .eq('user_id', profile.id)
              .eq('is_active', true)
              .single();

            // If table doesn't exist yet, just log out banned users
            if (banError && banError.code === '42P01') {
              console.log('user_bans table does not exist yet, logging out banned user');
              // Audit log for banned logout
              if (profile?.id) {
                await supabase.from('audit_logs').insert([
                  {
                    user_id: profile.id,
                    action: 'logout for banned',
                    target_id: profile.id,
                    target_type: 'Logout',
                    details: { email: user.email, reason: 'user_bans table missing', timestamp: new Date().toISOString() }
                  }
                ]).then(({ error }) => { if (error) console.error('Audit log insert error (banned, table missing):', error); });
              }
              await supabase.auth.signOut();
              setError('Your account has been banned. Please contact an administrator for assistance.');
              navigate('/login');
              return;
            }

            if (banRecord) {
              const now = new Date();
              const banEnd = new Date(banRecord.ban_end);
              
              if (now < banEnd) {
                // Audit log for banned logout
                if (profile?.id) {
                  await supabase.from('audit_logs').insert([
                    {
                      user_id: profile.id,
                      action: 'logout for banned',
                      target_id: profile.id,
                      target_type: 'Logout',
                      details: { email: user.email, reason: banRecord.reason, ban_end: banEnd.toISOString(), timestamp: new Date().toISOString() }
                    }
                  ]).then(({ error }) => { if (error) console.error('Audit log insert error (banned, active):', error); });
                }
                await supabase.auth.signOut();
                setError(`Your account has been banned. Reason: ${banRecord.reason}. Ban expires: ${banEnd.toLocaleString()}`);
                navigate('/login');
                return;
              } else {
                // Ban has expired, update status back to active
                await supabase
                  .from('profiles')
                  .update({ status: 'active' })
                  .eq('user_id', user.id);
                
                await supabase
                  .from('user_bans')
                  .update({ is_active: false })
                  .eq('user_id', profile.id)
                  .eq('is_active', true);
              }
            } else {
              // No active ban record found, but status is banned - log them out
              if (profile?.id) {
                await supabase.from('audit_logs').insert([
                  {
                    user_id: profile.id,
                    action: 'logout for banned',
                    target_id: profile.id,
                    target_type: 'Logout',
                    details: { email: user.email, reason: 'no active ban record', timestamp: new Date().toISOString() }
                  }
                ]).then(({ error }) => { if (error) console.error('Audit log insert error (banned, no record):', error); });
              }
              await supabase.auth.signOut();
              setError('Your account has been banned. Please contact an administrator for assistance.');
              navigate('/login');
              return;
            }
          } catch (error) {
            console.error('Error checking ban status:', error);
            // If there's any error checking ban status, log out banned users
            const { data: profile } = await supabase
              .from('profiles')
              .select('id')
              .eq('user_id', user.id)
              .single();
            if (profile?.id) {
              await supabase.from('audit_logs').insert([
                {
                  user_id: profile.id,
                  action: 'logout for banned',
                  target_id: profile.id,
                  target_type: 'Logout',
                  details: { email: user.email, reason: 'ban check error', error: error.message, timestamp: new Date().toISOString() }
                }
              ]).then(({ error }) => { if (error) console.error('Audit log insert error (banned, error):', error); });
            }
            await supabase.auth.signOut();
            setError('Your account has been banned. Please contact an administrator for assistance.');
            navigate('/login');
            return;
          }
        }

        // Check if user is archived
        if (profile && profile.status === 'archived') {
          if (profile?.id) {
            await supabase.from('audit_logs').insert([
              {
                user_id: profile.id,
                action: 'logout by unauthorized',
                target_id: profile.id,
                target_type: 'Logout',
                details: { email: user.email, reason: 'archived', timestamp: new Date().toISOString() }
              }
            ]).then(({ error }) => { if (error) console.error('Audit log insert error (archived):', error); });
          }
          await supabase.auth.signOut();
          setError('Your account has been archived. Please contact an administrator for assistance.');
          navigate('/login');
          return;
        }

        // Check if user is resident (existing logic)
        if (profile && profile.role === 'resident') {
          if (profile?.id) {
            await supabase.from('audit_logs').insert([
              {
                user_id: profile.id,
                action: 'logout by unauthorized',
                target_id: profile.id,
                target_type: 'Logout',
                details: { email: user.email, reason: 'resident', timestamp: new Date().toISOString() }
              }
            ]).then(({ error }) => { if (error) console.error('Audit log insert error (resident):', error); });
          }
          await supabase.auth.signOut();
          setError('Only admin and barangay users can log in. Residents are not allowed to access this portal.');
          navigate('/login');
        }
      }
    };

    checkUserProfile();
  }, [user, navigate]);

  // Add this useEffect to enforce single-session login
  useEffect(() => {
    const checkSession = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('current_session_id, id')
          .eq('user_id', user.id)
          .single();
        const localSessionId = localStorage.getItem('session_id');
        if (profile && profile.current_session_id && profile.current_session_id !== localSessionId) {
          if (profile?.id) {
            await supabase.from('audit_logs').insert([
              {
                user_id: profile.id,
                action: 'logout by unauthorized',
                target_id: profile.id,
                target_type: 'Logout',
                details: { email: user.email, reason: 'session conflict', timestamp: new Date().toISOString() }
              }
            ]).then(({ error }) => { if (error) console.error('Audit log insert error (session conflict):', error); });
          }
          await supabase.auth.signOut();
          localStorage.removeItem('session_id');
          sessionStorage.setItem('session_kicked', 'You have been logged out because your account was used to log in on another device or browser.');
          window.location.href = '/login';
        }
      }
    };
    checkSession();
  }, []);

  const value = {
    session,
    user,
    error,
    setError,
    signOut: async () => {
      let profileId = null;
      if (user) {
        // Fetch profile to get profile.id
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .single();
        profileId = profile?.id;
        if (profileId) {
          await supabase.from('audit_logs').insert([
            {
              user_id: profileId,
              action: 'user logout',
              target_id: profileId,
              target_type: 'Logout',
              details: { email: user.email, timestamp: new Date().toISOString() }
            }
          ]).then(({ error }) => { if (error) console.error('Audit log insert error (user logout):', error); });
        }
      }
      await supabase.auth.signOut();
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

// Utility to set session_id on login
export async function setSingleSession(userId) {
  const sessionId = uuidv4();
  localStorage.setItem('session_id', sessionId);
  await supabase
    .from('profiles')
    .update({ current_session_id: sessionId })
    .eq('user_id', userId);
}

// Utility to clear session_id on logout
export async function clearSingleSession(userId) {
  const localSessionId = localStorage.getItem('session_id');
  // Only clear if the DB value matches the local value
  const { data: profile } = await supabase
    .from('profiles')
    .select('current_session_id')
    .eq('user_id', userId)
    .single();
  if (profile && profile.current_session_id === localSessionId) {
    await supabase
      .from('profiles')
      .update({ current_session_id: null })
      .eq('user_id', userId);
  }
  localStorage.removeItem('session_id');
} 